<template>
  <div class="overflow-x-auto form-section no-padding" ref="refTableParent">
    <table ref="refTable">
      <thead>
        <tr>
          <th>{{ $t('Linked Account') }}</th>
          <th>{{ $t('Status') }}</th>
          <th class="actions sticky">{{ $t('Actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><div class="account-type email">{{ $t('Email') }}</div>
          </td>
          <td>{{ !bindData.emailInfo?.isBind ? $t('not set') : bindData.emailInfo.email }}</td>
          <td class="actions sticky">
            <el-button @click="handleBindEmailPhone('email', bindData.emailInfo?.isBind, bindData.emailInfo?.email)" size="small">{{ $t(!bindData.emailInfo?.isBind ? 'Set' : 'Change') }}</el-button>
          </td>
        </tr>
        <tr v-if="appStore.isCN">
          <td><div class="account-type phone">{{ $t('Phone Number') }}</div>
          </td>
          <td>{{ !bindData.phoneInfo?.isBind ? $t('not set') : bindData.phoneInfo.phone }}</td>
          <td class="actions sticky">
            <el-button @click="handleBindEmailPhone('phone', bindData.phoneInfo?.isBind, bindData.phoneInfo?.phone)" size="small">{{ $t(!bindData.phoneInfo?.isBind ? 'Set' : 'Change') }}</el-button>
          </td>
        </tr>
        <tr v-if="!appStore.isCN">
          <td><div class="account-type google">{{ $t('Google') }}</div>
          </td>
          <td>{{ !bindData.googleInfo?.isBind ? $t('not set-2') : bindData.googleInfo.googleEmail }}</td>
          <td class="actions sticky">
            <el-button @click="handleBindAppleGoogle('google', bindData.googleInfo?.isBind)" :disabled="isOnlyBindThisType('googleInfo')" size="small">{{ $t(!bindData.googleInfo?.isBind ? 'Link' : 'Unlink') }}</el-button>
          </td>
        </tr>
        <tr>
          <td><div class="account-type apple">{{ $t('Apple ID') }}</div>
          </td>
          <td>{{ !bindData.appleInfo?.isBind ? $t('not set-2') : bindData.appleInfo.appleEmail }}</td>
          <td class="actions sticky">
            <el-button @click="handleBindAppleGoogle('apple', bindData.appleInfo?.isBind)" :disabled="isOnlyBindThisType('appleInfo')" size="small">{{ $t(!bindData.appleInfo?.isBind ? 'Link' : 'Unlink') }}</el-button>
          </td>
        </tr>
        <tr v-if="appStore.isCN">
          <td><div class="account-type wechat">{{ $t('Wechat') }}</div>
          </td>
          <td>{{ !bindData.wxInfo?.isBind ? $t('not set-2') : (bindData.wxInfo.firstName || $t('linked')) }}</td>
          <td class="actions sticky">
            <el-button @click="handleAuthWechat(bindData.wxInfo?.isBind)" :disabled="isOnlyBindThisType('wxInfo')" size="small">{{ $t(!bindData.wxInfo?.isBind ? 'Link' : 'Unlink') }}</el-button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
  <AuthWechat ref="refAuthWechat" auth-type="bind" @on-success="handleGetBindInfo(true)"></AuthWechat>
  <AuthAppleGoogle ref="refAuthAppleGoogle" auth-type="bind" @on-success="handleGetBindInfo(false)"></AuthAppleGoogle>
  <AuthAccount ref="refAuthAccount" auth-type="bind" @on-success="handleBindEmailPhone(bindAccountType, false, bindAccount)"></AuthAccount>
  <BindAccount ref="refBindAccount" @on-success="handleGetBindInfo(true)"></BindAccount>
</template>

<script setup>
import { getBindThirdParties, unbindThirdParty } from '@/api/user/user';
import { appStore, userStore } from '@/stores';
import { computed, getCurrentInstance, nextTick, onMounted, reactive, ref } from 'vue';
import AuthAppleGoogle from './auth-apple-google.vue';
import gtagTools from '@/libs/gtag-tools';
import { ElMessage, ElMessageBox } from 'element-plus';
import AuthAccount from './auth-account.vue';
import BindAccount from './bind-account.vue';
import AuthWechat from './auth-wechat.vue';
import { isAppleEmail } from '@/libs/tools';
import { filter, keys } from 'lodash';
import useTableSticked from '@/view/user/center/hooks/useTableSticked';

const { $t } = getCurrentInstance().proxy

const userInfo = computed(() => userStore.userInfo)
const bindAccountType = ref('')
const bindAccount = ref('')

const bindData = reactive({
  appleInfo: null,
  googleInfo: null,
  wxInfo: null,
  emailInfo: null,
  phoneInfo: null,
})

/**
 * 判断是否只绑定了该方式，如果是，则禁止解绑
 * @param type 
 */
const isOnlyBindThisType = type => {
  const bindTypes = filter(keys(bindData), key => bindData[key] !== null && bindData[key]?.isBind)
  return bindTypes.length === 1 && bindTypes[0] === type
}

// 获取绑定信息
const handleGetBindInfo = async (showBindSuccessMsg = false) => {
  if (showBindSuccessMsg) {
    ElMessage.success($t('Binding success'))
  }
  try {
    const { data } = await getBindThirdParties()
    Object.assign(bindData, data)
    bindData.emailInfo = {
      isBind: isAppleEmail(userInfo.value.email) ? false : Boolean(userInfo.value.email),
      email: isAppleEmail(userInfo.value.email) ? '' : userInfo.value.email
    }
    bindData.phoneInfo = {
      isBind: Boolean(userInfo.value.phone),
      phone: userInfo.value.phone
    }
    if (appStore.isCN) {
      bindData.googleInfo = null
    } else {
      bindData.wxInfo = null
      bindData.phone = null
    }
  } catch (e) {
    console.log(e)
  }
}

/**
 * 谷歌苹果账号绑定和解绑
 * @param type 类型：google,apple
 * @param status 当前绑定状态
 */
const refAuthAppleGoogle = ref(null)
const handleBindAppleGoogle = async (type, status) => {
  bindAccountType.value = type
  if (status) {
    handleUnBindThirdParty(type)
  } else {
    refAuthAppleGoogle.value.open(type)
  }
}

// 绑定邮箱或手机号
const refAuthAccount = ref(null)
const refBindAccount = ref(null)
const handleBindEmailPhone = async (type, status, account) => {
  bindAccountType.value = type
  bindAccount.value = account
  // 已绑定时需要验证旧账号
  if (status) {
    refAuthAccount.value.open(type, account)
  } else {
    refBindAccount.value.open(type)
  }
}

// 绑定微信
const refAuthWechat = ref(null)
const handleAuthWechat = (status) => {
  if (!status) {
    refAuthWechat.value.open()
  } else {
    handleUnBindThirdParty('weixin')
  }
}
/**
 * 解绑第三方账号
 * @param type apple/google/wechat
 */
const handleUnBindThirdParty = async (type) => {
  const gtagReport = {
    apple: gtagTools.unlinkApple,
    google: gtagTools.unlinkGoogle,
    weixin: gtagTools.unlinkWechat
  }[type]
  ElMessageBox.confirm($t('Are you sure you want to untie them'), $t('Confirm'), {
    type: 'warning',
    center: true,
    beforeClose: async (action, instance, done) => {
      if (action === 'confirm') {
        instance.confirmButtonLoading = true
        try {
          await unbindThirdParty({ type })
          gtagReport(true)
          ElMessage({
            message: $t('Unbind successfully'),
            type: 'success',
          })
          handleGetBindInfo()
          done()
        } catch (e) {
          ElMessage.error($t('Unbinding failure'))
          console.log(e)
          gtagReport(false)
        }
        instance.confirmButtonLoading = false
      } else {
        done()
      }
    }
  })
}

const refTable = ref(null)
const refTableParent = ref(null)
useTableSticked(refTableParent, refTable)


onMounted(() => {
  handleGetBindInfo()
})
</script>

<style lang="scss" scoped>
table {
  th, td {
    padding: 14px 16px;
  }
}
.account-type {
  display: flex;
  align-items: center;
  &::before {
    content: '';
    display: block;
    width: 32px;
    height: 32px;
    margin-right: 12px;
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
  }
  &.email::before {
    background-image: url(@/assets/icon-bind-email.png);
  }
  &.phone::before {
    background-image: url(@/assets/icon-bind-phone.png);
  }
  &.google::before {
    background-image: url(@/assets/icon-bind-google.svg);
  }
  &.apple::before {
    background-image: url(@/assets/icon-bind-apple.svg);
  }
  &.wechat::before {
    background-image: url(@/assets/icon-bind-wechat.svg);
  }
}
.actions {
  width: 100px;
  padding: 0 24px;
  text-align: center;
  :deep(button) {
    width: 100px;
  }
}
</style>